import { useContext, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { ActiveView } from './constants';
import { isImageGenerationTemplate } from './utils';
import { FieldName } from './TemplatePromptForm/formConfigs';
import { getAiGeneratedImagesRemainingLimit } from '../../../reducers/aiAssistant.selectors';
import {
  getGeneratedImagesRemainingLimit as getGeneratedImagesRemainingLimitAction,
} from '../../../actions/aiAssistant.actions';
import useAction from '../../../utils/useAction';

/**
 * Shared hook that encapsulates the template prompt selection logic
 * used by both AIAssistantFreeDraftEntry (dialog) and AIToolsPageContent (page).
 */
const useTemplateSelection = () => {
  const {
    setActiveView,
    setSelectedTemplatePromptId,
    setTemplateLaunchPrefill,
    setImageGenerationLimitReachedAlertVisible,
    isPromptLimitReached,
    showPromptLimitAlert,
  } = useContext(aiAssistantContext);

  const remainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
  const hasRemainingLimit = !!remainingLimit && remainingLimit > 0;
  const fetchRemainingLimit = useAction(getGeneratedImagesRemainingLimitAction);

  useEffect(() => {
    fetchRemainingLimit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Core selection path shared by manual clicks and dashboard launch links. Returns
  // false (and surfaces the matching alert) when a limit guard blocks the select, so
  // a launch stays authoritative with the same limit behavior as a click.
  const selectTemplate = useCallback(
    (id: TemplatePrompts, prefill: Partial<Record<FieldName, string>> | null = null): boolean => {
      if (isPromptLimitReached) {
        showPromptLimitAlert();
        return false;
      }

      if (isImageGenerationTemplate(id) && !hasRemainingLimit) {
        setImageGenerationLimitReachedAlertVisible(true);
        return false;
      }

      setTemplateLaunchPrefill(prefill);
      setSelectedTemplatePromptId(id);
      setActiveView(ActiveView.TEMPLATE_PROMPT_FORM);
      return true;
    },
    [
      setActiveView, setSelectedTemplatePromptId, setTemplateLaunchPrefill, hasRemainingLimit,
      setImageGenerationLimitReachedAlertVisible, isPromptLimitReached, showPromptLimitAlert,
    ]
  );

  const handleTemplateClick = useCallback(
    (_title: string, _subheader: string, id: TemplatePrompts) => {
      selectTemplate(id);
    },
    [selectTemplate]
  );

  return { handleTemplateClick, selectTemplate };
};

export default useTemplateSelection;
